import axios from 'axios'
import { type NavigationGuardReturn, type RouteLocationNormalizedLoaded, type RouteLocationNormalized, START_LOCATION } from 'vue-router'
import { AGENT_ROUTE_NAMES } from '@/agent/types/router'
import configResolver, { pathCombine } from '@/common/helpers/configResolver'
import { initApplicationSettings } from '@/common/services/ApplicationService'
import { triggerOidcChangePassword } from '@/common/services/AuthenticationService'
import { type NavigationGuardWithoutNext } from '@/app/types/router'

import { useContextualSearchStore } from '@/search/stores/contextualSearchStore'
import { useSessionStore } from '@/common/stores/sessionStore'
import { useCaseStore } from '@/agent/stores/caseStore'
import { initCaseCardLayout } from '@/agent/composables/useCaseCardLayout'
import { useSearchStore } from '@/search/stores/searchStore'
import { useNavigationStore } from '@/common/stores/navigationStore'
import { useCtiStore } from '@/cti/stores/ctiStore'

import { clear as clearLocalData } from '@/common/services/LocalStorageService'
import { checkNavigationConsistent, handleNavigation, NAVIGATION_HISTORY_KEY, resetNavigationHistory } from '@/common/services/NavigationService'
import localizationItemServerData from '@/common/services/serverData/localizationItemServerData'
import { buildCustomerCtiQueryParams } from '@/cti/helpers/customerCtiQuery'

import { PrefixTranslationsEnum } from '@/common/types/i18n'
import { useRolesStore } from '@/common/stores/rolesStore'
import { useCompanyStore } from '@/agent/stores/companyStore'

import { initCaseWorklog } from '@/agent/composables/useWorklog'

async function handleChangePasswordRoute(originPath: string, oidcEnabled: boolean) {
  if (!oidcEnabled) return

  await triggerOidcChangePassword(pathCombine([configResolver.root, originPath]))
}

export const contextualSearchBeforeEnter = async (): Promise<NavigationGuardReturn> => {
  useContextualSearchStore().resetContextualSearch()
}

export const changePasswordRouteBeforeEnter = async (
  _: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded,
): Promise<NavigationGuardReturn> => {
  const sessionStore = useSessionStore()
  await handleChangePasswordRoute(from.path, sessionStore.oidcEnabled)
}

export const agentBeforeEnter: NavigationGuardWithoutNext = async (_, from) => {
  if (from.meta.agent) return

  clearLocalData(['session', 'parameters', NAVIGATION_HISTORY_KEY])

  if (from?.meta?.admin) {
    initApplicationSettings()

    const searchStore = useSearchStore()
    searchStore.$reset()
  }

  /**
   * Prefetch and merge translations asynchronously
   */
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.TYPOLOGY],
  })
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.REASON],
  })
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.SKILL],
  })
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.PRIORITY],
  })

  /**
   * Hydrate the app synchronously for data needed immediately
   */
  const hydrationRequests = [
    useRolesStore().loadRoles(),
    localizationItemServerData.fetch({
      prefixes: [PrefixTranslationsEnum.USERSTATE, PrefixTranslationsEnum.SCREEN, PrefixTranslationsEnum.SPACE],
    }),
  ]
  await Promise.all(hydrationRequests)
}

export const caseBeforeEnter = () => {
  useCompanyStore().$reset()
  useCaseStore().$reset()
  useNavigationStore().$reset()

  initCaseCardLayout()
}

export function customerCtiQueryBeforeEnter(to: RouteLocationNormalized): NavigationGuardReturn {
  const ctiStore = useCtiStore()
  if (!ctiStore.hasOngoingUnlinkedCtiCall) return true

  const ctiQuery = buildCustomerCtiQueryParams(ctiStore.ctiCallInfo?.requestInfo?.customer)
  const hasAllCtiParams = Object.keys(ctiQuery).every((key) => to.query[key])
  if (hasAllCtiParams) return true

  return {
    name: to.name,
    params: to.params,
    query: { ...to.query, ...ctiQuery },
    hash: to.hash,
  }
}

export const caseWorklogGuard = async (to: RouteLocationNormalized): Promise<NavigationGuardReturn> => {
  const caseId = Number.parseInt(to.params.caseId as string)
  try {
    await initCaseWorklog(caseId)
  } catch (error) {
    if (axios.isAxiosError(error) && error.request?.status === 403) {
      return { name: AGENT_ROUTE_NAMES.CASE_ACCESS_DENIED, params: { caseId } }
    }
  }
}

export const navigationBeforeResolve: NavigationGuardWithoutNext = async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded) => {
  if (to.query.mergedCustomerSucceed) {
    // after a merge customer between two existing customers
    // we clear the navigation history because the last path can be the page of the customer deleted
    resetNavigationHistory()
    return
  }
  if (from.meta?.admin || to.meta?.admin || from === START_LOCATION || from.path === to.path) return

  if (to.meta?.allowBackNavigation) {
    // Skip create→edit only for non-CTI create validation (INNSO-22060).
    // CTI Search Customer keeps customer/new in history so Back restores the search.
    const isCustomerCreateToEdit = from.path === '/assist/customer/new' && to.path.startsWith('/assist/customer/edit/') && !from.query.isCti
    if (!from.meta?.skipHistory && !isCustomerCreateToEdit) {
      handleNavigation(from.path)
    }
    checkNavigationConsistent(to.path)
  } else {
    resetNavigationHistory()
  }
}
